import { useState, useMemo } from 'react';
import { useCachedResource } from '../hooks/useCachedResource';
import { usePageRefresh } from '../context/PullToRefreshContext';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import TaskCard from '../components/TaskCard';
import { requirementsFromFlags } from '../components/RequirementPills';
import { useUserLocation } from '../hooks/useUserLocation';
import { routesApi, type MyRoutesResponse } from '../services/api';

// Pace calculation helper
type PaceStatus = 'on-track' | 'watch' | 'behind';

function usePaceInfo(summary: MyRoutesResponse['summary'] | undefined) {
  return useMemo(() => {
    if (!summary) return null;

    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const dayOfMonth = today.getDate();
    const daysRemaining = daysInMonth - dayOfMonth;

    const expectedProgress = (dayOfMonth / daysInMonth) * summary.total_visits_required;
    const actualProgress = summary.total_visits_completed;

    let paceStatus: PaceStatus;
    if (actualProgress >= expectedProgress * 0.75) {
      paceStatus = 'on-track';
    } else if (actualProgress >= expectedProgress * 0.5) {
      paceStatus = 'watch';
    } else {
      paceStatus = 'behind';
    }

    const dailyRateNeeded = daysRemaining > 0
      ? summary.total_visits_remaining / daysRemaining
      : summary.total_visits_remaining;

    return { paceStatus, daysRemaining, dailyRateNeeded };
  }, [summary]);
}

/* COMMENTED OUT: Progress bar component - was used by _RouteCard
function ProgressBar({ completed, total }: { completed: number; total: number }) {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 rounded-full bg-slate-200">
        <div
          className={`h-2 rounded-full transition-all ${
            percentage >= 100 ? 'bg-green-500' : percentage >= 50 ? 'bg-primary-500' : 'bg-amber-500'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      <span className="text-xs font-medium text-slate-600">{percentage}%</span>
    </div>
  );
}
*/

/* COMMENTED OUT: Route card component - replaced by TaskCard
function _RouteCard({ route, onClick }: { route: Route; onClick: () => void }) {
  const isComplete = route.visits_completed >= route.frequency;
  const lastVisit = route.last_visit_date
    ? new Date(route.last_visit_date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : 'Never';

  return (
    <button
      onClick={onClick}
      className="w-full rounded-xl bg-white p-4 text-left shadow-card transition-all hover:shadow-card-hover active:scale-[0.98]"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-800">{route.store.store_name}</span>
            {isComplete && (
              <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <p className="mt-0.5 text-sm text-slate-500">
            {route.store.city}, {route.store.state}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            <span className="font-mono text-primary-600">#{route.project_id}</span>
            {' '}{route.project_description}
          </p>
        </div>
        <div className="text-right">
          <div className={`text-lg font-bold ${isComplete ? 'text-green-600' : 'text-slate-800'}`}>
            {route.visits_completed}/{route.frequency}
          </div>
          <div className="text-xs text-slate-500">visits</div>
        </div>
      </div>

      <div className="mt-3">
        <ProgressBar completed={route.visits_completed} total={route.frequency} />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-slate-500">
          Last visit: <span className="font-medium text-slate-700">{lastVisit}</span>
        </span>
        {route.hours_per_visit && (
          <span className="text-slate-500">
            {route.hours_per_visit}h per visit
          </span>
        )}
      </div>
    </button>
  );
}
*/

// Month selector component
function MonthSelector({
  value,
  onChange,
}: {
  value: string;
  onChange: (month: string) => void;
}) {
  const today = new Date();
  const months: { value: string; label: string }[] = [];

  // Generate last 3 months and next 2 months
  for (let i = -3; i <= 2; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    months.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    });
  }

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2">
      {months.map((month) => (
        <button
          key={month.value}
          onClick={() => onChange(month.value)}
          className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all ${
            value === month.value
              ? 'bg-primary-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {month.label}
        </button>
      ))}
    </div>
  );
}

export default function Routes() {
  const navigate = useNavigate();
  const { userLocation } = useUserLocation();
  // Default to current month
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);

  // Filter state
  const [filterStatus, setFilterStatus] = useState<'all' | 'incomplete' | 'complete'>('all');
  const [sortBy, setSortBy] = useState<'store' | 'progress' | 'lastVisit'>('store');

  // Cache-first routes for the selected month (#2057): paint the last-cached
  // month instantly, revalidate in the background.
  const {
    data: routesData,
    isLoading,
    error,
    refetch: refetchRoutes,
  } = useCachedResource<MyRoutesResponse>(
    `routes:${selectedMonth}`,
    () => routesApi.getMyRoutes(selectedMonth),
    [selectedMonth]
  );

  // #2089: swipe-down revalidates the selected month's routes.
  usePageRefresh(refetchRoutes);

  // Filter and sort routes
  const filteredRoutes = routesData?.routes
    .filter((route) => {
      if (filterStatus === 'incomplete') return route.visits_completed < route.frequency;
      if (filterStatus === 'complete') return route.visits_completed >= route.frequency;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'progress') {
        const aProgress = a.frequency > 0 ? a.visits_completed / a.frequency : 0;
        const bProgress = b.frequency > 0 ? b.visits_completed / b.frequency : 0;
        return aProgress - bProgress; // Incomplete first
      }
      if (sortBy === 'lastVisit') {
        const aDate = a.last_visit_date ? new Date(a.last_visit_date).getTime() : 0;
        const bDate = b.last_visit_date ? new Date(b.last_visit_date).getTime() : 0;
        return aDate - bDate; // Oldest first
      }
      // Default: store name
      return a.store.store_name.localeCompare(b.store.store_name);
    }) || [];

  // Calculate pace info
  const paceInfo = usePaceInfo(routesData?.summary);

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <PageHeader
        title="My Calls"
        subtitle={
          isLoading
            ? 'Loading...'
            : routesData?.summary
            ? `${routesData.summary.total_visits_completed} of ${routesData.summary.total_visits_required} visits completed`
            : 'No calls assigned'
        }
        onBack={() => navigate(-1)}
      />

      {/* Month Selector */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />
      </div>

      {/* Summary Card */}
      {routesData?.summary && !isLoading && (
        <div className="mx-4 mt-4 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 p-4 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Overall Progress</p>
              <p className="mt-1 text-3xl font-bold">
                {routesData.summary.completion_percentage}%
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-90">Visits</p>
              <p className="mt-1 text-xl font-semibold">
                {routesData.summary.total_visits_completed} / {routesData.summary.total_visits_required}
              </p>
            </div>
          </div>
          <div className="mt-3">
            <div className="h-2 rounded-full bg-white/30">
              <div
                className="h-2 rounded-full bg-white transition-all"
                style={{ width: `${Math.min(routesData.summary.completion_percentage, 100)}%` }}
              />
            </div>
          </div>
          <div className="mt-3 flex justify-between text-sm">
            <span>
              <span className="font-semibold">{routesData.summary.total_stores}</span> stores
            </span>
            <span>
              <span className="font-semibold">{routesData.summary.total_visits_remaining}</span> remaining
            </span>
          </div>
        </div>
      )}

      {/* Pace Indicator */}
      {paceInfo && routesData?.summary && routesData.summary.total_visits_remaining > 0 && !isLoading && (
        <div className={`mx-4 mt-3 flex items-start gap-2 rounded-lg p-3 ${
          paceInfo.paceStatus === 'on-track' ? 'bg-green-100' :
          paceInfo.paceStatus === 'watch' ? 'bg-amber-100' : 'bg-red-100'
        }`}>
          {paceInfo.paceStatus === 'on-track' ? (
            <svg className="h-4 w-4 flex-shrink-0 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : paceInfo.paceStatus === 'watch' ? (
            <svg className="h-4 w-4 flex-shrink-0 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ) : (
            <svg className="h-4 w-4 flex-shrink-0 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          <div className="flex-1">
            <div className={`text-sm font-medium ${
              paceInfo.paceStatus === 'on-track' ? 'text-green-700' :
              paceInfo.paceStatus === 'watch' ? 'text-amber-700' : 'text-red-700'
            }`}>
              {paceInfo.paceStatus === 'on-track' ? 'On track' :
               paceInfo.paceStatus === 'watch' ? 'Watch pace' : 'Behind pace'}
              {paceInfo.daysRemaining > 0 && (
                <span className="font-normal">
                  {' '}&middot; {paceInfo.daysRemaining} day{paceInfo.daysRemaining !== 1 ? 's' : ''} left
                </span>
              )}
            </div>
            {paceInfo.daysRemaining > 0 && (
              <div className={`text-xs ${
                paceInfo.paceStatus === 'on-track' ? 'text-green-700' :
                paceInfo.paceStatus === 'watch' ? 'text-amber-700' : 'text-red-700'
              } opacity-80`}>
                Need ~{paceInfo.dailyRateNeeded.toFixed(1)} visits/day to complete on time
              </div>
            )}
            {paceInfo.daysRemaining === 0 && (
              <div className={`text-xs ${
                paceInfo.paceStatus === 'on-track' ? 'text-green-700' :
                paceInfo.paceStatus === 'watch' ? 'text-amber-700' : 'text-red-700'
              } opacity-80`}>
                Last day of month - {routesData.summary.total_visits_remaining} visits still needed
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      {routesData?.routes && routesData.routes.length > 0 && !isLoading && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="all">All Calls</option>
              <option value="incomplete">Incomplete</option>
              <option value="complete">Complete</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="store">Sort by Store</option>
              <option value="progress">Sort by Progress</option>
              <option value="lastVisit">Sort by Last Visit</option>
            </select>
          </div>
        </div>
      )}

      {/* Routes List */}
      <main className="p-4">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="rounded-xl bg-red-50 p-4 text-center text-red-700">
            <p className="font-medium">Unable to load calls</p>
            <p className="mt-1 text-sm">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && (!routesData?.routes || routesData.routes.length === 0) && (
          <div className="rounded-xl bg-slate-100 p-8 text-center">
            <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-200">
              <svg className="h-6 w-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-700">No Calls Assigned</h3>
            <p className="mt-1 text-sm text-slate-500">
              You don't have any calls assigned for {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.
            </p>
          </div>
        )}

        {/* Routes List */}
        {!isLoading && !error && filteredRoutes.length > 0 && (
          <div className="space-y-3">
            {filteredRoutes.map((route) => (
              <TaskCard
                key={route.route_id}
                type="call"
                store={{
                  store_id: route.store.store_id,
                  store_name: route.store.store_name,
                  legacy_store_id: route.store.legacy_store_id ? parseInt(route.store.legacy_store_id, 10) : undefined,
                  address: route.store.address,
                  city: route.store.city,
                  state: route.store.state,
                  lat: route.store.lat,
                  lon: route.store.lon,
                }}
                projectId={route.project_id}
                projectDescription={route.project_description}
                status={route.visits_completed >= route.frequency ? 'completed' : 'pending'}
                onClick={() => navigate(`/call/${route.route_id}`)}
                visitsCompleted={route.visits_completed}
                visitsRequired={route.frequency}
                hoursPerVisit={route.hours_per_visit ?? undefined}
                lastVisitDate={route.last_visit_date ?? undefined}
                requirements={requirementsFromFlags(route)}
                userLocation={userLocation}
              />
            ))}
          </div>
        )}

        {/* No results from filter */}
        {!isLoading && !error && routesData?.routes && routesData.routes.length > 0 && filteredRoutes.length === 0 && (
          <div className="rounded-xl bg-slate-50 p-6 text-center">
            <p className="text-slate-500">No calls match the current filter.</p>
          </div>
        )}
      </main>
    </div>
  );
}
